/**
 * Letter Hive — parity with Flutter `letter_hive_logic.dart`.
 * Daily hive uses `letter-hive-{YYYY-M-D}` + `hashStringJs32` (same as Word Master date hash style).
 */

export const LETTER_HIVE_MIN_LENGTH = 4;
export const LETTER_HIVE_PANGRAM_BONUS = 7;

/** Local calendar key with no zero-padding (matches Flutter `dateKeyForHash`). */
export function getDateKeyForHash(d = new Date()) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Same 32-bit string hash as Flutter `WordMasterWordUtils.hashStringJs32`. */
export function hashStringJs32(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
}

export function letterHiveDailySeed(localDate = new Date()) {
  const key = getDateKeyForHash(localDate);
  return Math.abs(hashStringJs32(`letter-hive-${key}`));
}

export function uniqueLettersUpper(w) {
  const s = new Set();
  for (let i = 0; i < w.length; i++) {
    s.add(w[i].toUpperCase());
  }
  return s;
}

/**
 * @param {Iterable<string>} allWords — uppercase dictionary words
 * @returns {string[]} sorted pangram-capable source words
 */
export function pangramSourceWords(allWords) {
  const out = [];
  for (const w of allWords) {
    if (w.length >= 7 && uniqueLettersUpper(w).size === 7) {
      out.push(w);
    }
  }
  out.sort();
  return out;
}

export function pickHiveFromCandidates({ sortedCandidates, seed, practice }) {
  if (!sortedCandidates.length) {
    throw new Error('No Letter Hive candidates in dictionary');
  }
  let word;
  let center;
  const letters = new Set();
  if (practice) {
    word = sortedCandidates[Math.floor(Math.random() * sortedCandidates.length)];
    for (let i = 0; i < word.length; i++) {
      letters.add(word[i].toUpperCase());
    }
    const sorted = [...letters].sort();
    center = sorted[Math.floor(Math.random() * 7)];
  } else {
    word = sortedCandidates[seed % sortedCandidates.length];
    for (let i = 0; i < word.length; i++) {
      letters.add(word[i].toUpperCase());
    }
    const sorted = [...letters].sort();
    center = sorted[seed % 7];
  }
  const sortedLetters = [...letters].sort();
  const hiveKey = `${sortedLetters.join('')}|${center}`;
  return {
    letters,
    center,
    sourceWord: word.toUpperCase(),
    hiveKey,
  };
}

export function isValidHiveWord(word, hiveLetters, center) {
  const w = word.trim().toUpperCase();
  if (w.length < LETTER_HIVE_MIN_LENGTH) return false;
  if (!/^[A-Z]+$/.test(w)) return false;
  if (!w.includes(center)) return false;
  for (let i = 0; i < w.length; i++) {
    if (!hiveLetters.has(w[i])) return false;
  }
  return true;
}

export function isPangram(word, hiveLetters) {
  const u = uniqueLettersUpper(word);
  return u.size === hiveLetters.size && [...hiveLetters].every((c) => u.has(c));
}

export function baseScoreForWord(word) {
  const l = word.trim().toUpperCase().length;
  if (l < LETTER_HIVE_MIN_LENGTH) return 0;
  if (l === 4) return 1;
  return l;
}

export function scoreForWord(word, hiveLetters, center) {
  if (!isValidHiveWord(word, hiveLetters, center)) return 0;
  const w = word.trim().toUpperCase();
  let s = baseScoreForWord(w);
  if (isPangram(w, hiveLetters)) {
    s += LETTER_HIVE_PANGRAM_BONUS;
  }
  return s;
}

export function totalScoreForFound(foundSet, hiveLetters, center) {
  let t = 0;
  for (const w of foundSet) {
    t += scoreForWord(w, hiveLetters, center);
  }
  return t;
}

/** Ring letters: non-center hive letters, sorted (hive layout). */
export function ringLetters(hive) {
  return [...hive.letters].filter((c) => c !== hive.center).sort();
}
